import { useState, ReactElement, KeyboardEvent } from "react";
import { Stack, TextField, Typography, IconButton } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import CheckIcon from "@mui/icons-material/Check";
import CancelIcon from "@mui/icons-material/Cancel";

interface EditableTextFieldProps {
  /**
   * Current value to display/edit
   */
  value: string;
  /**
   * Callback when value is saved
   */
  onSave: (newValue: string) => Promise<void> | void;
  /**
   * Callback when editing is cancelled (optional)
   */
  onCancel?: () => void;
  /**
   * Whether the field can be edited
   * @default true
   */
  editable?: boolean;
  /**
   * Whether the save operation is in progress
   * @default false
   */
  saving?: boolean;
  /**
   * TextField label (shown above the field)
   */
  label?: string;
  /**
   * Typography variant for display mode
   * @default "body1"
   */
  variant?: "body1" | "body2" | "h6" | "h5" | "h4";
  /**
   * Placeholder text for the TextField
   */
  placeholder?: string;
  /**
   * Validation function - return true if value is valid
   */
  validate?: (value: string) => boolean;
  /**
   * Custom error message for validation
   */
  validationError?: string;
}

/**
 * Reusable editable text field component
 * Shows text with edit icon, switches to TextField with check/cancel icons when editing
 */
function EditableTextField({
  value,
  onSave,
  onCancel,
  editable = true,
  saving = false,
  label,
  variant = "body1",
  placeholder,
  validate,
  validationError,
}: EditableTextFieldProps): ReactElement {
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [editedValue, setEditedValue] = useState<string>(value);
  const [error, setError] = useState<string>("");

  const handleEdit = (): void => {
    if (!editable || saving) return;
    setEditedValue(value);
    setError("");
    setIsEditing(true);
  };

  const handleCancel = (): void => {
    setEditedValue(value);
    setError("");
    setIsEditing(false);
    if (onCancel) {
      onCancel();
    }
  };

  const handleSave = async (): Promise<void> => {
    const trimmedValue = editedValue.trim();

    // Validation
    if (validate && !validate(trimmedValue)) {
      setError(validationError || "Invalid value");
      return;
    }

    if (!trimmedValue) {
      setError("Value cannot be empty");
      return;
    }

    if (trimmedValue === value) {
      setIsEditing(false);
      return;
    }

    try {
      await onSave(trimmedValue);
      setIsEditing(false);
      setError("");
    } catch (err) {
      console.error("Error saving:", err);
      setError(err instanceof Error ? err.message : "Failed to save");
    }
  };

  const handleKeyPress = (e: KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === "Enter") {
      handleSave();
    } else if (e.key === "Escape") {
      handleCancel();
    }
  };

  return (
    <Stack spacing={0.5}>
      {label && (
        <Typography variant="body2" color="text.secondary" gutterBottom>
          {label}
        </Typography>
      )}
      {isEditing ? (
        <Stack direction="row" spacing={1} alignItems="center">
          <TextField
            value={editedValue}
            onChange={(e) => {
              setEditedValue(e.target.value);
              setError("");
            }}
            variant="outlined"
            size="small"
            fullWidth
            disabled={saving}
            error={!!error}
            helperText={error}
            placeholder={placeholder}
            onKeyDown={handleKeyPress}
            autoFocus
          />
          <IconButton
            onClick={handleSave}
            disabled={saving || !editedValue.trim() || editedValue.trim() === value || (validate && !validate(editedValue.trim()))}
            color="primary"
            size="small"
          >
            <CheckIcon />
          </IconButton>
          <IconButton onClick={handleCancel} disabled={saving} size="small">
            <CancelIcon />
          </IconButton>
        </Stack>
      ) : (
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant={variant}>{value}</Typography>
          {editable && (
            <IconButton onClick={handleEdit} size="small" disabled={saving}>
              <EditIcon fontSize="small" />
            </IconButton>
          )}
        </Stack>
      )}
    </Stack>
  );
}

export default EditableTextField;
